import Messenger from "@/common/Messenger";
import * as documentReady from "@/common/utils/documentReady";
import urlUtils from "@/common/utils/urlUtils";
import { constants } from "@/modules/Constants";
import ExperienceAttributes, {
  attrNames,
  shownAsType,
} from "@/modules/ExperienceAttributes";
import { warmDefaultPlayIcons } from "@/modules/assetImageCache";
import * as embedUtils from "@/modules/embedUtils";
import { releasePublisherWarmVideo } from "@/modules/experiencePrefetchWarm";
import { hostScriptOrigin } from "@/modules/hostScriptOrigin";
import { startPixieForRecipes } from "@/modules/pixieAutoload";
import { warmPlayShell } from "@/modules/playShellWarm";
import { isRecipeId } from "@/modules/recipeId";
import type { MessengerCallback } from "@/common/Messenger";
import type { LegacyMessage } from "@/types/messages";
import { setupEmbedExperiencePrefetch } from "@/popover/anchorPrefetch";
import {
  kickMediaWarmInBackground,
  setupPixmotoAnchor,
  setupPixmotoAnchorSizes,
} from "@/popover/anchorSetup";
import {
  buildOverlayPlayUrl,
  cacheKeyFromAttrs,
  normalizeOverlayUrl,
  overlayExperienceKey,
  resolvePrefetchExperienceId,
} from "@/popover/overlayUrls";
import {
  pauseOverlayIframe,
  postPrefetchToIframeWindow,
  requestOverlayAutoplay,
} from "@/popover/overlayMessages";
import { setupOverlayFrame } from "@/popover/overlayFrame";
import {
  POPOVER_CLOSE_EVENT,
  POPOVER_OPEN_EVENT,
  announce,
} from "@/popover/overlayEvents";
import { must } from "@/common/utils/must";
import type { PopoverAnchor } from "@/popover/types";

// Full build: on a publisher origin that uses Session Replay, an expired recording in the shared registry loads this recorder.
import mixpanel from "mixpanel-browser";
import { isAdminPreviewSurface } from "@/modules/previewSurface";

const mixpanelToken = process.env.MIXPANEL_TOKEN;
const mixpanelDebug = process.env.MIXPANEL_DEBUG === true;
/** Safety net only — overlay reveals on child `initialized`, not after this delay. */
const OVERLAY_MEDIA_READY_FALLBACK_MS = 4000;
const POPOVER_SRC = /\/js\/popover\.js(?:[?#]|$)/;
// Read now: `currentScript` is null once this first run is over.
const SCRIPT_ORIGIN = hostScriptOrigin(document.currentScript, POPOVER_SRC);

if (mixpanelToken && !isAdminPreviewSurface()) {
  mixpanel.init(mixpanelToken, {
    debug: mixpanelDebug,
    track_pageview: false,
    persistence: "localStorage",
  });
  mixpanel.identify(Date.now());
}

class Popover {
  declare _coverModal: HTMLDivElement | null;
  declare _iFrame: HTMLIFrameElement | null;
  // Never assigned; _onChildError reads it.
  declare _iFrameDiv: HTMLDivElement | null;
  declare _spinner: HTMLDivElement | null;
  declare _anchors: ArrayLike<Element>;
  declare _autospawn: string | false | undefined;
  declare _idautospawn: string | true | null | undefined;
  declare _messenger: Messenger | null | undefined;
  declare _lastOverlayExperienceId: string | true | null | undefined;
  declare _overlayReadyFallbackTimer: ReturnType<typeof setTimeout> | null;
  declare _iframeLoadedUrl: string | null;
  declare _iframeLoadedExperienceKey: string | null;
  declare _overlayWantAutoplay: boolean;
  declare _isMobile: boolean;
  declare _bodyOverflow: string;
  declare _overlayOpen: boolean;

  constructor() {
    if (mixpanelToken && !isAdminPreviewSurface()) {
      window.mixpanel = mixpanel;
    }

    this._coverModal = null;
    this._iFrame = null;
    this._iFrameDiv = null;
    this._spinner = null;
    this._anchors = [];
    this._autospawn = false;
    this._idautospawn = null;
    this._messenger = null;
    this._lastOverlayExperienceId = null;
    this._overlayReadyFallbackTimer = null;

    this._onDocumentReady = this._onDocumentReady.bind(this);
    this._onChildInitialized = this._onChildInitialized.bind(this);
    this._onChildMediaReady = this._onChildMediaReady.bind(this);
    this._onChildExit = this._onChildExit.bind(this);
    this._onChildError = this._onChildError.bind(this);
    this._onUnload = this._onUnload.bind(this);
    this._iframeLoadedUrl = null;
    this._iframeLoadedExperienceKey = null;
    this._overlayWantAutoplay = false;
    this._overlayOpen = false;
  }

  initialize() {
    documentReady.ready(this._onDocumentReady, this);
    this._setupMessenger();
  }

  reload() {
    documentReady.ready(this._onDocumentReady, this);
  }

  _onDocumentReady() {
    warmDefaultPlayIcons();
    warmPlayShell(new URL(SCRIPT_ORIGIN).host);

    const forceMobile = embedUtils.getUrlParameter("pix-mobile");
    if (forceMobile) {
      this._isMobile = true;
    } else {
      this._isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        );
    }

    this._setupPixmotoAnchors();

    window.addEventListener(
      "resize",
      function (this: Popover, event: Event) {
        for (let k = 0; k < this._anchors.length; k++) {
          setupPixmotoAnchorSizes(this._anchors[k] as PopoverAnchor);
        }
      }.bind(this),
    );

    this._autospawn = embedUtils.getUrlParameter("pix-autospawn");

    if (this._autospawn === "true") {
      this._idautospawn =
        embedUtils.getUrlParameter("pix-expid") ||
        embedUtils.getUrlParameter("pix-experience-id");
      const urlautospawn = embedUtils.getUrlParameter("pix-url");
      let target: {
        anchor: PopoverAnchor;
        attrs: ExperienceAttributes;
      } | null = null;
      if (this._idautospawn) {
        for (let k = 0; k < this._anchors.length; k++) {
          const a = this._anchors[k] as PopoverAnchor;
          const aAttrs = must(a.pixmoto, "popover anchor setup").attrs;
          const idexperience = aAttrs.getExperienceId();
          if (String(idexperience) === String(this._idautospawn)) {
            target = { anchor: a, attrs: aAttrs };
            break;
          }
        }
      } else if (urlautospawn) {
        for (let kk = 0; kk < this._anchors.length; kk++) {
          const a2 = this._anchors[kk] as PopoverAnchor;
          const a2Attrs = must(a2.pixmoto, "popover anchor setup").attrs;
          const anchorUrl = a2Attrs.getUrl();
          if (anchorUrl === urlautospawn) {
            target = { anchor: a2, attrs: a2Attrs };
            break;
          }
        }
      } else if (this._anchors.length > 0) {
        const first = this._anchors[0] as PopoverAnchor;
        const firstAttrs = must(first.pixmoto, "popover anchor setup").attrs;
        target = { anchor: first, attrs: firstAttrs };
        this._idautospawn = firstAttrs.getExperienceId();
      }

      if (target) {
        this.popover(target.anchor, target.attrs, true);
      }
    }

    setupEmbedExperiencePrefetch();
    startPixieForRecipes(this._anchors, SCRIPT_ORIGIN);
  }

  _onChildInitialized(
    messenger: Messenger,
    event: MessageEvent,
    data: LegacyMessage,
  ) {
    this._sendPrefetchedExperienceToIframe(event);
    /* Drop parent spinner once play shell mounted; child has its own until API JSON arrives. */
    this._revealOverlayIframe();
    requestOverlayAutoplay(this, event);
  }

  _onChildMediaReady(
    messenger: Messenger,
    event: MessageEvent,
    data: LegacyMessage,
  ) {
    releasePublisherWarmVideo();
    this._revealOverlayIframe();
    requestOverlayAutoplay(this, event);
  }

  _sendPrefetchedExperienceToIframe(event: MessageEvent) {
    if (!event || !event.source || !event.source.postMessage) {
      return;
    }
    if (typeof event.origin !== "string" || event.origin.length === 0) {
      return;
    }
    postPrefetchToIframeWindow(this, event.source, event.origin);
  }

  /**
   * Public hook used by the module-level prefetch resolver to push the
   * just-cached payload into an already-open overlay iframe. Safe to call
   * when no overlay is mounted — guards on _iFrame and parses origin from
   * the live iframe src.
   */
  _postPrefetchToIframeWindowExternal() {
    if (!this._iFrame || !this._iFrame.src) {
      return;
    }
    if (this._iFrame.src.indexOf("about:") === 0) {
      return;
    }
    try {
      const origin = new URL(this._iFrame.src, window.location.href).origin;
      postPrefetchToIframeWindow(this, this._iFrame.contentWindow, origin);
    } catch {
      /* ignore */
    }
  }

  _onChildExit(messenger: Messenger, event: MessageEvent, data: LegacyMessage) {
    if (this._iFrame) {
      document.body.style.overflow = this._bodyOverflow;
      this._hideOverlay();
    } else {
      const window = event.source as Window;
      window.close();
    }
  }

  _onChildError(
    messenger: Messenger,
    event: MessageEvent,
    data: LegacyMessage,
  ) {
    this._clearOverlayReadyFallback();
    if (this._iFrame) {
      this._iFrame.style.left = "0px";
    }
    if (this._iFrameDiv) {
      this._iFrameDiv.style.left = "0px";
    }
    if (this._spinner) {
      this._spinner.style.display = "none";
    }
  }

  _onUnload(e?: Event) {
    this._tearDownDesktop();
  }

  _setOverlayOpen(open: boolean) {
    if (this._overlayOpen !== open) {
      this._overlayOpen = open;
      announce(open ? POPOVER_OPEN_EVENT : POPOVER_CLOSE_EVENT);
    }
  }

  _hideOverlay() {
    this._setOverlayOpen(false);
    pauseOverlayIframe(this._iFrame);
    this._clearOverlayReadyFallback();
    if (this._coverModal) {
      this._coverModal.style.left = "-999999px";
    }
    if (this._iFrame) {
      this._iFrame.style.left = "-999999px";
    }
    if (this._spinner) {
      this._spinner.style.display = "none";
    }
  }

  _setupPixmotoAnchors() {
    this._anchors = document.getElementsByClassName("pixmoto-popover-anchor");
    if (this._anchors.length === 0) {
      this._anchors = document.getElementsByClassName("pixmoto_popover_anchor");
    }

    for (let a = 0; a < this._anchors.length; a++) {
      setupPixmotoAnchor(this, this._anchors[a] as PopoverAnchor);
    }
  }

  _showOverlaySpinner() {
    if (this._spinner) {
      this._spinner.style.display = "block";
    }
    if (this._iFrame) {
      this._iFrame.style.left = "-999999px";
    }
  }

  _revealOverlayIframe() {
    this._clearOverlayReadyFallback();
    if (this._iFrame) {
      this._iFrame.style.left = "0px";
    }
    if (this._spinner) {
      this._spinner.style.display = "none";
    }
  }

  _clearOverlayReadyFallback() {
    if (this._overlayReadyFallbackTimer) {
      clearTimeout(this._overlayReadyFallbackTimer);
      this._overlayReadyFallbackTimer = null;
    }
  }

  _scheduleOverlayReadyFallback() {
    this._clearOverlayReadyFallback();
    this._overlayReadyFallbackTimer = setTimeout(
      function (this: Popover) {
        this._revealOverlayIframe();
      }.bind(this),
      OVERLAY_MEDIA_READY_FALLBACK_MS,
    );
  }

  _tearDownDesktop() {
    this._setOverlayOpen(false);
    this._clearOverlayReadyFallback();
    if (this._coverModal) {
      this._coverModal.remove();
    }

    if (this._iFrame) {
      this._iFrame.src = "about:blank";
    }
    this._coverModal = null;
    this._iFrame = null;
    this._spinner = null;
    this._iframeLoadedUrl = null;
    this._iframeLoadedExperienceKey = null;
    this._overlayWantAutoplay = false;
    releasePublisherWarmVideo();

    window.removeEventListener("beforeunload", this._onUnload);
  }

  _openOverlayWithWarm(attrs: ExperienceAttributes, isAuto: boolean) {
    const cacheKey = cacheKeyFromAttrs(attrs);

    // setupOverlayFrame sets _coverModal and _iFrame together; teardown clears both.
    let coverModal = this._coverModal;
    let iFrame = this._iFrame;
    if (!coverModal || !iFrame) {
      const frame = setupOverlayFrame(this);
      coverModal = frame.coverModal;
      iFrame = frame.iFrame;
    }

    this._bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    coverModal.style.left = "0px";
    this._showOverlaySpinner();
    this._setOverlayOpen(true);

    if (cacheKey) {
      kickMediaWarmInBackground(cacheKey, attrs);
    }

    const url = buildOverlayPlayUrl(attrs, isAuto, cacheKey);
    this._loadOverlayIframe(url, iFrame);
  }

  _loadOverlayIframe(url: string, iFrame: HTMLIFrameElement) {
    const normalized = normalizeOverlayUrl(url);
    const experienceKey = overlayExperienceKey(url);
    const current = this._iFrame ? normalizeOverlayUrl(this._iFrame.src) : "";

    if (
      this._iFrame &&
      this._iframeLoadedExperienceKey === experienceKey &&
      current.indexOf("about:") !== 0
    ) {
      this._revealOverlayIframe();
      try {
        const reuseOrigin = new URL(current, window.location.href).origin;
        postPrefetchToIframeWindow(
          this,
          this._iFrame.contentWindow,
          reuseOrigin,
        );
      } catch (_e) {
        /* ignore */
      }
      return;
    }

    this._iframeLoadedUrl = normalized;
    this._iframeLoadedExperienceKey = experienceKey;
    this._scheduleOverlayReadyFallback();
    iFrame.src = url;

    const self = this;
    iFrame.addEventListener(
      "load",
      function onOverlayIframeLoad() {
        if (
          !self._iFrame ||
          !self._iFrame.src ||
          self._iFrame.src.indexOf("about:") === 0
        ) {
          return;
        }
        try {
          const origin = new URL(self._iFrame.src, window.location.href).origin;
          postPrefetchToIframeWindow(self, self._iFrame.contentWindow, origin);
        } catch {
          /* ignore */
        }
      },
      { once: true },
    );
  }

  _setupMessenger(targetWindow?: Window, targetOrigin?: string) {
    this._messenger = new Messenger(window, targetWindow, targetOrigin);
    this._messenger.setName("popover");
    this._messenger.setPrefix(constants.MESSAGE_PREFIX);
    this._messenger.onMessengerHandler = {
      onInitialized: this._onChildInitialized as MessengerCallback,
      onMediaReady: this._onChildMediaReady as MessengerCallback,
      onExit: this._onChildExit as MessengerCallback,
      onError: this._onChildError as MessengerCallback,
    };
    this._messenger.startListening();
  }

  /**
   * Plays a recipe with no anchor on the page (pixie.js plays Pixie's picks
   * this way). `attrs` are embed attributes by short name, as on an anchor;
   * the page's `?pix-*` settings apply as for an anchor, under `attrs`.
   */
  open(experienceId: unknown, attrs?: Record<string, string>): boolean {
    const id = String(experienceId == null ? "" : experienceId).trim();
    if (!isRecipeId(id) || !document.body) {
      return false;
    }
    const all = ExperienceAttributes.attrsFromQuery(
      urlUtils.querySettings(),
      true,
    );
    const keys = attrs ? Object.keys(attrs) : [];
    for (let i = 0; i < keys.length; i++) {
      const value = (attrs as Record<string, unknown>)[keys[i]];
      if (typeof value === "string" && value) {
        all[keys[i]] = value;
      }
    }
    all[attrNames.EXPERIENCE_ID] = id;
    this.popover(null, new ExperienceAttributes(all), false);
    return true;
  }

  isOpen() {
    return this._overlayOpen;
  }

  popover(
    anchor: PopoverAnchor | null,
    attrs: ExperienceAttributes,
    isAuto: boolean,
  ) {
    // Autoplay is the only supported behavior for popover embeds — always
    // play, even when an existing embed carries data-pix-autoplay="false".
    this._overlayWantAutoplay = true;
    this._lastOverlayExperienceId =
      attrs.getExperienceId() || resolvePrefetchExperienceId(attrs);
    const isMobile = this._isMobile || attrs.getIsForcedMobile();
    let other: string | true | null;

    if (isMobile) {
      other = attrs.mobileTargetWindow();
      if (other) {
        if (other === "_overlay") {
          this._openOverlayWithWarm(attrs, isAuto);
        } else {
          window.open(
            attrs.getPlayUrl(isAuto, shownAsType.WINDOW),
            String(other),
          );
        }
      } else {
        (window as { location: string | Location }).location = attrs.getPlayUrl(
          isAuto,
          shownAsType.PAGE,
        );
      }
    } else {
      other = attrs.targetWindow();
      if (other && other !== "_overlay") {
        window.open(
          attrs.getPlayUrl(isAuto, shownAsType.WINDOW),
          String(other),
        );
      } else {
        this._openOverlayWithWarm(attrs, isAuto);
      }
    }
  }
}

const pixmoto: NonNullable<typeof window.__pixmoto> = window.__pixmoto || {};
const popover = new Popover();
if (pixmoto.popover === undefined) {
  popover.initialize();
  pixmoto.popover = popover;
}

window.__pixmoto = pixmoto;

export default popover;
